import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Ship } from './entities/Ship';
import { Bullet } from './entities/Bullet';
import { Asteroid } from './entities/Asteroid';
import { UFO } from './entities/UFO';
import { PhysicsSystem } from './systems/PhysicsSystem';
import { AsteroidManager } from './systems/AsteroidManager';

export class AsteroidsGame extends BaseGame {
  private ship: Ship | null = null;
  private bullets: Bullet[] = [];
  private asteroids: Asteroid[] = [];
  private ufos: UFO[] = [];
  
  private physicsSystem: PhysicsSystem;
  private asteroidManager: AsteroidManager;
  private nextBulletId: number = 1;
  
  // Game state
  private gameStarted: boolean = false;
  private waveComplete: boolean = false;
  private invulnerable: boolean = false;
  private invulnerabilityTime: number = 0;
  private readonly INVULNERABILITY_DURATION: number = 3; // 3 seconds after respawn
  
  // UFO spawning
  private ufoSpawnTimer: number = 0;
  private ufoSpawnInterval: number = 20; // UFO every 20 seconds
  
  // Hyperspace
  private hyperspaceCharges: number = 3;
  
  // Bonus ship tracking (from official manual: bonus ship every 10,000 points)
  private lastBonusScore: number = 0;
  private readonly BONUS_SHIP_INTERVAL: number = 10000;

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 900, height: 600, fps: 60, scale: 1 }; // Landscape for mobile
    super(renderManager, audioManager, gameConfig, 'space-debris');
    
    this.physicsSystem = new PhysicsSystem(this.config.width, this.config.height);
    this.asteroidManager = new AsteroidManager(this.config.width, this.config.height, renderManager, audioManager);
  }

  public init(): void {
    console.log('Asteroids Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    this.waveComplete = false;
    this.invulnerable = false;
    this.invulnerabilityTime = 0;
    this.hyperspaceCharges = 3;
    this.ufoSpawnTimer = 0;
    this.lastBonusScore = 0;
    
    // Clear arrays
    this.bullets = [];
    this.asteroids = [];
    this.ufos = [];
    this.nextBulletId = 1;
    
    // Create player ship
    this.createShip();
    
    // Initialize asteroids for this wave
    this.asteroidManager.initializeWave(this.level);
    this.asteroids = this.asteroidManager.getAsteroids();
  }

  private createShip(): void {
    const startX = this.config.width / 2;
    const startY = this.config.height / 2;
    
    this.ship = new Ship(
      'player_ship',
      startX,
      startY,
      this.renderManager,
      this.audioManager
    );
    
    // Make ship invulnerable for a few seconds
    this.invulnerable = true;
    this.invulnerabilityTime = this.INVULNERABILITY_DURATION;
  }

  public update(deltaTime: number): void {
    if (this.isGameOver) return;
    
    if (!this.gameStarted || !this.ship) return;
    
    // Update invulnerability timer
    if (this.invulnerable) {
      this.invulnerabilityTime -= deltaTime;
      if (this.invulnerabilityTime <= 0) {
        this.invulnerable = false;
      }
    }
    
    // Update UFO spawn timer
    this.ufoSpawnTimer += deltaTime;
    
    // Update ship
    if (this.ship.active) {
      this.ship.update(deltaTime);
      this.physicsSystem.updateEntity(this.ship, deltaTime);
    }
    
    // Update bullets
    this.updateBullets(deltaTime);
    
    // Update asteroids
    this.updateAsteroids(deltaTime);
    
    // Update UFOs
    this.updateUFOs(deltaTime);
    
    // Spawn UFO if needed
    if (this.shouldSpawnUFO()) {
      this.spawnUFO();
    }
    
    // Check collisions
    this.checkCollisions();
    
    // Check win/lose conditions
    this.checkWaveConditions();
  }

  private updateBullets(deltaTime: number): void {
    this.bullets.forEach(bullet => {
      bullet.update(deltaTime);
      this.physicsSystem.updateEntity(bullet, deltaTime);
    });
    this.bullets = this.bullets.filter(bullet => bullet.active);
  }

  private updateAsteroids(deltaTime: number): void {
    this.asteroids.forEach(asteroid => {
      asteroid.update(deltaTime);
      this.physicsSystem.updateEntity(asteroid, deltaTime);
    });
    this.asteroids = this.asteroids.filter(asteroid => asteroid.active);
  }

  private updateUFOs(deltaTime: number): void {
    this.ufos.forEach(ufo => {
      if (this.ship && this.ship.active) {
        ufo.setTarget(this.ship.position);
      }
      ufo.update(deltaTime);
      this.physicsSystem.updateEntity(ufo, deltaTime);
      
      // UFO shooting
      const ufoBullet = ufo.tryShoot();
      if (ufoBullet) {
        this.bullets.push(ufoBullet);
      }
    });
    this.ufos = this.ufos.filter(ufo => ufo.active);
  }

  private shouldSpawnUFO(): boolean {
    return this.ufoSpawnTimer >= this.ufoSpawnInterval &&
           this.ufos.length === 0 &&
           this.asteroids.length > 0; // Only spawn UFOs when asteroids are present
  }

  private spawnUFO(): void {
    const side = Math.random() < 0.5 ? 'left' : 'right';
    const startX = side === 'left' ? -50 : this.config.width + 50;
    const startY = Math.random() * this.config.height;
    
    // Determine UFO type based on score
    const isLarge = this.score < 40000; // Large UFOs appear early, small ones later
    
    const ufo = new UFO(
      `ufo_${Date.now()}`,
      startX,
      startY,
      isLarge,
      this.renderManager,
      this.audioManager,
      this.config.width,
      this.config.height
    );
    
    this.ufos.push(ufo);
    this.ufoSpawnTimer = 0;
    
    console.log(`Spawned ${isLarge ? 'large' : 'small'} UFO at (${startX}, ${startY})`);
  }

  private checkCollisions(): void {
    if (!this.ship || !this.ship.active || this.invulnerable) return;

    // Check ship vs asteroids
    this.asteroids.forEach(asteroid => {
      if (!asteroid.active) return;
      
      if (this.checkCircleCollision(this.ship!, asteroid)) {
        this.destroyShip();
      }
    });

    // Check ship vs UFOs
    this.ufos.forEach(ufo => {
      if (!ufo.active) return;
      
      if (this.checkCircleCollision(this.ship!, ufo)) {
        this.destroyShip();
        ufo.destroy();
      }
    });

    // Check bullets vs asteroids
    this.bullets.forEach(bullet => {
      if (!bullet.active || bullet.isEnemyBullet()) return;

      this.asteroids.forEach(asteroid => {
        if (!asteroid.active) return;
        
        if (this.checkCircleCollision(bullet, asteroid)) {
          bullet.destroy();
          const points = asteroid.getPointValue();
          this.addScore(points);
          
          // Split asteroid
          const fragments = asteroid.split();
          fragments.forEach(fragment => this.asteroids.push(fragment));
          
          this.audioManager.playExplosion();
        }
      });

      // Check bullets vs UFOs
      this.ufos.forEach(ufo => {
        if (!ufo.active) return;
        
        if (this.checkCircleCollision(bullet, ufo)) {
          bullet.destroy();
          ufo.destroy();
          this.addScore(ufo.getPointValue());
          this.audioManager.playExplosion();
        }
      });
    });

    // Check UFO bullets vs ship
    this.bullets.forEach(bullet => {
      if (!bullet.active || !bullet.isEnemyBullet()) return;
      
      if (this.checkCircleCollision(bullet, this.ship!)) {
        bullet.destroy();
        this.destroyShip();
      }
    });
  }

  private checkCircleCollision(entity1: any, entity2: any): boolean {
    const dx = entity1.position.x - entity2.position.x;
    const dy = entity1.position.y - entity2.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const combinedRadius = (entity1.radius || 8) + (entity2.radius || 8);
    
    return distance < combinedRadius;
  }

  private destroyShip(): void {
    if (!this.ship) return;
    
    this.ship.destroy();
    this.loseLife();
    this.audioManager.playExplosion();
    
    if (this.lives > 0) {
      // Respawn after delay
      setTimeout(() => {
        this.createShip();
      }, 2000);
    }
  }

  private checkWaveConditions(): void {
    // Wave complete when all asteroids are destroyed
    const activeAsteroids = this.asteroids.filter(a => a.active);
    
    if (activeAsteroids.length === 0 && !this.waveComplete) {
      this.waveComplete = true;
      this.completeWave();
    }
  }

  private completeWave(): void {
    console.log(`Wave ${this.level} complete!`);
    
    // Bonus points for remaining ships
    const shipBonus = this.lives * 1000;
    this.addScore(shipBonus);
    
    // Clear UFOs and bullets
    this.ufos = [];
    this.bullets = [];
    
    // Start next wave after delay
    setTimeout(() => {
      this.nextLevel();
      this.startNextWave();
    }, 3000);
  }

  private startNextWave(): void {
    this.waveComplete = false;
    this.hyperspaceCharges = 3; // Restore hyperspace charges
    this.ufoSpawnTimer = 0;
    
    // Initialize asteroids for new wave
    this.asteroidManager.initializeWave(this.level);
    this.asteroids = this.asteroidManager.getAsteroids();
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen with space background
    this.renderManager.clear('#000000');
    
    // Render asteroids
    this.asteroids.forEach(asteroid => {
      if (asteroid.active) {
        asteroid.render(ctx);
      }
    });
    
    // Render UFOs
    this.ufos.forEach(ufo => {
      if (ufo.active) {
        ufo.render(ctx);
      }
    });
    
    // Render bullets
    this.bullets.forEach(bullet => {
      if (bullet.active) {
        bullet.render(ctx);
      }
    });
    
    // Render ship (with invulnerability flashing)
    if (this.ship && this.ship.active) {
      if (!this.invulnerable || Math.floor(this.invulnerabilityTime * 10) % 2 === 0) {
        this.ship.render(ctx);
      }
    }
    
    // Render HUD
    this.renderHUD();
    
    // Render game state messages
    if (this.waveComplete) {
      this.renderWaveComplete();
    }
    
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  protected renderHUD(): void {
    // Score
    this.renderManager.drawText(
      `SCORE: ${this.score.toString().padStart(6, '0')}`,
      20,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', this.config.width - 200, 20, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      // Draw small ship icons for lives
      this.drawLifeIcon(this.config.width - 120 + i * 25, 30);
    }
    
    // Wave
    this.renderManager.drawTextCentered(
      `WAVE ${this.level}`,
      this.config.width / 2,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Hyperspace charges
    this.renderManager.drawText(
      `HYPERSPACE: ${this.hyperspaceCharges}`,
      20,
      this.config.height - 20,
      '#00FFFF',
      '12px "Courier New", monospace'
    );
  }

  private drawLifeIcon(x: number, y: number): void {
    // Draw a small ship icon
    const points = [
      { x: 0, y: -6 },
      { x: -4, y: 4 },
      { x: -2, y: 2 },
      { x: 2, y: 2 },
      { x: 4, y: 4 }
    ];
    
    this.renderManager.drawPolygon(points.map(p => ({ x: x + p.x, y: y + p.y })), '#FFFFFF', false);
  }

  private renderWaveComplete(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      `WAVE ${this.level} COMPLETE!`,
      centerX,
      centerY - 40,
      '#FFFFFF',
      '32px "Courier New", monospace'
    );
    
    const shipBonus = this.lives * 1000;
    
    this.renderManager.drawTextCentered(
      `SHIP BONUS: ${shipBonus}`,
      centerX,
      centerY + 20,
      '#00FFFF',
      '18px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }
    
    if (!this.ship || !this.ship.active) return;
    
    // Ship rotation
    if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      this.ship.rotateLeft();
    }
    if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      this.ship.rotateRight();
    }
    
    // Ship thrust
    if (input.keys['ArrowUp'] || input.keys['KeyW']) {
      this.ship.thrust();
    }
    
    // Shooting
    if (input.keys['Space'] || input.touch.gesture === 'tap') {
      const bullet = this.ship.shoot();
      if (bullet) {
        bullet.id = `bullet_${this.nextBulletId++}`;
        this.bullets.push(bullet);
      }
    }
    
    // Hyperspace
    if (input.keys['KeyH'] && this.hyperspaceCharges > 0) {
      this.useHyperspace();
    }
  }

  // Override addScore to check for bonus ships (every 10,000 points)
  public addScore(points: number): void {
    const oldScore = this.score;
    super.addScore(points);
    
    // Check for bonus ship
    const bonusShips = Math.floor(this.score / this.BONUS_SHIP_INTERVAL) - Math.floor(oldScore / this.BONUS_SHIP_INTERVAL);
    if (bonusShips > 0) {
      this.lives += bonusShips;
      console.log(`Bonus ship awarded! Lives: ${this.lives}`);
      this.audioManager.playPowerUp(); // Play bonus sound
    }
  }

  private useHyperspace(): void {
    if (!this.ship) return;
    
    this.hyperspaceCharges--;
    
    // Random teleport with small chance of destruction
    if (Math.random() < 0.1) { // 10% chance of destruction
      this.destroyShip();
      return;
    }
    
    // Teleport to random location
    const newX = Math.random() * this.config.width;
    const newY = Math.random() * this.config.height;
    
    this.ship.hyperspace(newX, newY);
    this.audioManager.playPowerUp(); // Placeholder sound
    
    // Brief invulnerability
    this.invulnerable = true;
    this.invulnerabilityTime = 1.0;
    
    console.log(`Hyperspace used! Charges remaining: ${this.hyperspaceCharges}`);
  }

  public cleanup(): void {
    super.cleanup();
    this.bullets = [];
    this.asteroids = [];
    this.ufos = [];
    this.ship = null;
  }
}